import type { AxiosProgressEvent } from 'axios';
import api from './api';
import type {
  ScholarshipDocumentSlot,
  ScholarshipDraftResponse,
  ScholarshipDocumentUploadResponse,
  ScholarshipDocumentDeleteResponse,
  ScholarshipSubmitPayload,
  ScholarshipSubmitResponse,
} from '../components/scholarshipApplication/types';

class ScholarshipGrantService {
  /**
   * Initialize or retrieve a draft token for the scholarship application session
   */
  async createDraft(): Promise<ScholarshipDraftResponse> {
    const response = await api.post<ScholarshipDraftResponse>(
      '/scholarship/applications/draft',
      {}
    );
    return response.data;
  }

  /**
   * Upload a document file to the draft application slot.
   * Lets axios determine the multipart/form-data boundary automatically.
   */
  async uploadDocument(
    draftToken: string,
    slot: ScholarshipDocumentSlot,
    file: File,
    onProgress?: (progressEvent: AxiosProgressEvent) => void
  ): Promise<ScholarshipDocumentUploadResponse> {
    const formData = new FormData();
    formData.append('draft_token', draftToken);
    formData.append('slot', slot);
    formData.append('file', file);

    const response = await api.post<ScholarshipDocumentUploadResponse>(
      '/scholarship/applications/draft/documents',
      formData,
      {
        // The shared `api` instance defaults every request to
        // `Content-Type: application/json`. Axios treats that as a signal to
        // serialise FormData through formDataToJSON (dropping the File), so the
        // header must be cleared here to let the browser emit a real multipart
        // body with its boundary. Without this the backend returns 422.
        headers: { 'Content-Type': null },
        onUploadProgress: onProgress,
      }
    );
    return response.data;
  }

  /**
   * Remove an uploaded document from a draft slot.
   */
  async removeDocument(
    draftToken: string,
    slot: ScholarshipDocumentSlot
  ): Promise<ScholarshipDocumentDeleteResponse> {
    const response = await api.delete<ScholarshipDocumentDeleteResponse>(
      `/scholarship/applications/draft/documents/${slot}`,
      {
        params: { draft_token: draftToken },
      }
    );
    return response.data;
  }

  /**
   * Submit the completed scholarship application
   */
  async submit(
    payload: ScholarshipSubmitPayload
  ): Promise<ScholarshipSubmitResponse> {
    const response = await api.post<ScholarshipSubmitResponse>(
      '/scholarship/applications/submit',
      payload
    );
    return response.data;
  }

  async getEnglishForm(token: string): Promise<ScholarshipEnglishForm> {
    const response = await api.get<ScholarshipEnglishForm>(`/scholarship/english/${encodeURIComponent(token)}`);
    return response.data;
  }

  async saveEnglishForm(token: string, payload: ScholarshipEnglishSave): Promise<ScholarshipEnglishForm> {
    const response = await api.put<ScholarshipEnglishForm>(`/scholarship/english/${encodeURIComponent(token)}`, payload);
    return response.data;
  }
}

export type ScholarshipEnglishCertificateType = 'moi' | 'ielts' | 'toefl' | 'other' | 'none';

export interface ScholarshipEnglishForm {
  full_name: string;
  reference_number: string;
  certificate_type: ScholarshipEnglishCertificateType | null;
  certificate_other: string | null;
  score: string | null;
  issued_on: string | null;
  submitted_at: string | null;
  updated_at: string | null;
}

export interface ScholarshipEnglishSave {
  certificate_type: ScholarshipEnglishCertificateType;
  certificate_other?: string | null;
  score?: string | null;
  issued_on?: string | null;
}

export default new ScholarshipGrantService();
